import { useMemo } from 'react';
import * as THREE from 'three';

function Guide({
  convRatio,
  guideRadius,
  guideHeight,
  guideThickness,
  color,
  drawingBaseDimensions,
}) {
  const reverseConfig = ['A', 'C', 'E', 'G', 'L'].includes(
    drawingBaseDimensions.configuration
  )
    ? 1
    : -1;

  const turns = drawingBaseDimensions.nbr_windings * reverseConfig;

  const inclinationLe =
    THREE.MathUtils.degToRad(drawingBaseDimensions.slope_angle_le) *
    reverseConfig;
  const inclinationLe_db = inclinationLe / 2;
  const heightDiffLE =
    (Math.tan(inclinationLe) *
      reverseConfig *
      drawingBaseDimensions.le_angular +
      Math.tan(inclinationLe_db) *
        drawingBaseDimensions.le_double_bend_value *
        reverseConfig) *
    convRatio;

  const inclination =
    THREE.MathUtils.degToRad(drawingBaseDimensions.slope_angle_center) *
    reverseConfig;
  const inclination_db = inclination / 2;
  const heightDiffUE =
    (Math.tan(inclination) * reverseConfig * drawingBaseDimensions.ue_angular +
      Math.tan(inclination_db) *
        drawingBaseDimensions.ue_double_bend_value *
        reverseConfig) *
    convRatio;

  const h2 = drawingBaseDimensions.h2;
  const height = convRatio * h2 - heightDiffLE - heightDiffUE;
  const radius = convRatio * guideRadius;
  const segments = 36 * parseInt(Math.abs(turns), 10);
  const rectWidth = convRatio * guideThickness;
  const rectHeight =
    convRatio *
    (parseInt(guideHeight, 10) + drawingBaseDimensions.winding_thickness);

  const { vertices, indices } = useMemo(() => {
    const verts = [];
    const inds = [];
    const halfWidth = rectWidth / 2;
    const halfHeight = rectHeight / 2;

    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const angle = t * turns * Math.PI * 2;
      const x = radius * Math.cos(angle);
      const z = radius * Math.sin(angle);
      const y = t * height;
      const nextT = t + 0.01;
      const nextAngle = nextT * turns * Math.PI * 2;
      const nextX = radius * Math.cos(nextAngle);
      const nextZ = radius * Math.sin(nextAngle);
      const nextY = nextT * height;
      const tangent = new THREE.Vector3(
        nextX - x,
        nextY - y,
        nextZ - z
      ).normalize();
      const normal = new THREE.Vector3(0, 1, 0).cross(tangent).normalize();
      const binormal = tangent.clone().cross(normal).normalize();
      const center = new THREE.Vector3(x, y, z);
      const corner1 = center
        .clone()
        .add(normal.clone().multiplyScalar(halfWidth))
        .add(binormal.clone().multiplyScalar(-halfHeight)); // Top-left
      const corner2 = center
        .clone()
        .add(normal.clone().multiplyScalar(halfWidth))
        .add(binormal.clone().multiplyScalar(halfHeight)); // Top-right
      const corner3 = center
        .clone()
        .add(normal.clone().multiplyScalar(-halfWidth))
        .add(binormal.clone().multiplyScalar(halfHeight)); // Bottom-right
      const corner4 = center
        .clone()
        .add(normal.clone().multiplyScalar(-halfWidth))
        .add(binormal.clone().multiplyScalar(-halfHeight)); // Bottom-left

      verts.push(
        corner1.x,
        corner1.y,
        corner1.z,
        corner2.x,
        corner2.y,
        corner2.z,
        corner3.x,
        corner3.y,
        corner3.z,
        corner3.x,
        corner3.y,
        corner3.z,
        corner4.x,
        corner4.y,
        corner4.z,
        corner1.x,
        corner1.y,
        corner1.z
      );

      const baseIndex = i * 6;
      inds.push(baseIndex, baseIndex + 1, baseIndex + 2);
      inds.push(baseIndex, baseIndex + 2, baseIndex + 4);

      if (i > 0) {
        const prevBaseIndex = baseIndex - 6;
        inds.push(prevBaseIndex + 1, baseIndex, baseIndex + 1);
        inds.push(prevBaseIndex + 1, prevBaseIndex, baseIndex);
        inds.push(baseIndex + 4, baseIndex + 2, prevBaseIndex + 4);
        inds.push(baseIndex + 2, prevBaseIndex + 2, prevBaseIndex + 4);
        inds.push(prevBaseIndex, baseIndex, baseIndex + 4);
        inds.push(prevBaseIndex, baseIndex + 4, prevBaseIndex + 4);
        inds.push(prevBaseIndex + 2, baseIndex + 2, baseIndex + 1);
        inds.push(prevBaseIndex + 2, baseIndex + 1, prevBaseIndex + 1);
      }
    }

    return {
      vertices: new Float32Array(verts),
      indices: new Uint32Array(inds),
    };
  }, [turns, height, radius, segments, rectWidth, rectHeight]);

  const geometry = useMemo(() => {
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geom.computeVertexNormals();
    return geom;
  }, [vertices]);

  const sideGeometry = useMemo(() => {
    const geom = geometry.clone();
    geom.setIndex(new THREE.BufferAttribute(indices, 1));
    geom.computeVertexNormals();
    return geom;
  }, [geometry, indices]);

  return (
    <group
      position={[
        0,
        (drawingBaseDimensions.h1 +
          parseInt(guideHeight, 10) / 2 -
          drawingBaseDimensions.winding_thickness / 2) *
          convRatio +
          heightDiffLE,
        0,
      ]}
    >
      <mesh geometry={sideGeometry}>
        <meshStandardMaterial
          color={color}
          roughness={0.8}
          metalness={0.5}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
}

export default Guide;
